import React, { useEffect, useState } from 'react'
import { Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Slider, TextField } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CustomMultipleSelect, { OptionType } from '../shared/CustomMultipleSelect'
import { useTranslation } from 'react-i18next'
import { dedupeSkills } from './skillUtils'

interface Skill extends OptionType {
  matchScore: number
  points?: number
}

interface SkillsSectionProps {
  selectedSkills: Skill[]
  handleAddSkill: (newSkills: Skill[]) => void
  availableSkills: Skill[]
  isMatched: boolean
  onSkillSearch?: (value: string) => void
}

const SkillsSection = ({ selectedSkills, handleAddSkill, availableSkills, isMatched, onSkillSearch }: SkillsSectionProps) => {
  const { t } = useTranslation()
  const [openSkillDialog, setOpenSkillDialog] = useState<boolean>(false)
  const [totalPoints, setTotalPoints] = useState<number>(0)

  const handlePointChange = (index: number, newValue: number) => {
    const updatedSkills = [...selectedSkills]
    const otherSkillsTotal = updatedSkills.reduce(
      (acc, skill, idx) => (idx !== index ? acc + (skill.points ?? skill.matchScore ?? 0) : acc),
      0
    )

    const maxAllowedPoints = 100 - otherSkillsTotal
    const nextValue = Math.min(newValue, maxAllowedPoints)
    updatedSkills[index].points = nextValue
    updatedSkills[index].matchScore = nextValue

    const uniqueSkills = dedupeSkills(updatedSkills)

    handleAddSkill(uniqueSkills)
    setTotalPoints(uniqueSkills.reduce((acc, skill) => acc + (skill.points ?? skill.matchScore ?? 0), 0))
  }

  const renderSkillChips = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        <Typography
          variant="caption"
          color={'grey'}>
          {t('positions.skillsSection.instructions')}
        </Typography>
        {selectedSkills.map((skill, index) => {
          const otherSkillsTotal = selectedSkills.reduce(
            (acc, s, idx) => (idx !== index ? acc + (s.points ?? s.matchScore ?? 0) : acc),
            0
          )
          const maxAllowedPoints = 100 - otherSkillsTotal

          return (
            <Box
              key={skill.name}
              sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Box sx={{ minWidth: '120px' }}>
                <Chip
                  variant="outlined"
                  label={skill.label || skill.name}
                  sx={{
                    fontWeight: 'bold',
                    color: skill.matchScore >= 7 ? 'success.dark' : skill.matchScore >= 5 ? 'warning.dark' : 'grey.600'
                  }}
                />
              </Box>

              <Slider
                value={skill.points ?? skill.matchScore ?? 0}
                onChange={(e, newValue) => handlePointChange(index, newValue as number)}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                disabled={totalPoints >= 100 && (skill.points ?? skill.matchScore ?? 0) === 0}
                scale={(x) => x}
              />
              <TextField
                value={skill.points ?? skill.matchScore ?? 0}
                onChange={(e) => handlePointChange(index, Math.min(Number(e.target.value), maxAllowedPoints))}
                type="number"
                size="small"
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 80 }}
              />
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <div>
      <Typography variant="h6">{t('positions.table.skills')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {selectedSkills.length > 0 && renderSkillChips()}
        <Chip
          variant="outlined"
          color="primary"
          icon={<AddIcon />}
          onClick={() => setOpenSkillDialog(true)}
          label={t('positions.skillsSection.addSkills')}
          sx={{ fontWeight: 'bold' }}
          disabled={selectedSkills.length >= 10}
        />
      </Box>
      <Typography
        variant="caption"
        color={totalPoints > 100 ? 'error' : 'textSecondary'}>
      {t('positions.skillsSection.totalPoints', { total: totalPoints })}
    </Typography>
      {totalPoints > 100 && (
        <Typography
          variant="body2"
          color="error">
          {t('positions.skillsSection.pointsCannotExceed')}
        </Typography>
      )}

      <SkillDialog
        open={openSkillDialog}
        handleAddSkill={handleAddSkill}
        setOpenSkillDialog={setOpenSkillDialog}
        availableSkills={dedupeSkills(availableSkills)}
        selectedSkills={selectedSkills}
        isMatched={isMatched}
        onSkillSearch={onSkillSearch}
      />
    </div>
  )
}

const SkillDialog = ({
  open,
  handleAddSkill,
  setOpenSkillDialog,
  availableSkills,
  selectedSkills,
  isMatched,
  onSkillSearch
}: {
  open: boolean
  handleAddSkill: (skills: Skill[]) => void
  setOpenSkillDialog: (open: boolean) => void
  availableSkills: Skill[]
  selectedSkills: Skill[]
  isMatched: boolean
  onSkillSearch?: (value: string) => void
}) => {
  const { t } = useTranslation()
  const [localSelectedSkills, setLocalSelectedSkills] = useState<Skill[]>(selectedSkills)

  useEffect(() => {
    setLocalSelectedSkills(dedupeSkills(selectedSkills))
  }, [open, selectedSkills])

  const handleSave = () => {
    handleAddSkill(dedupeSkills(localSelectedSkills))
    setOpenSkillDialog(false)
  }

  const handleChange = (newSkills: OptionType | OptionType[] | null) => {
    let updatedSkills: Skill[] = []

    if (Array.isArray(newSkills)) {
      updatedSkills = newSkills.map((skill) => {
        const existingSkill = selectedSkills.find((s) => s.name === skill.name)
        if (existingSkill) {
          return { ...existingSkill }
        }

        const availableSkill = availableSkills.find((s) => s.name === skill.name)
        if (availableSkill) {
          return { ...availableSkill, matchScore: availableSkill.matchScore ?? 0, points: availableSkill.points ?? availableSkill.matchScore ?? 0 }
        }

        return { name: skill.name, label: skill.label || skill.name, matchScore: 0, points: 0 }
      })
    } else if (newSkills) {
      const existingSkill = selectedSkills.find((s) => s.name === newSkills.name)
      if (existingSkill) {
        updatedSkills = [{ ...existingSkill }]
      } else {
        const foundSkill = availableSkills.find((s) => s.name === newSkills.name)
        if (foundSkill) {
          updatedSkills = [{ ...foundSkill, matchScore: foundSkill.matchScore ?? 0, points: foundSkill.points ?? foundSkill.matchScore ?? 0 }]
        } else {
          updatedSkills = [{ name: newSkills.name, label: newSkills.label || newSkills.name, matchScore: 0, points: 0 }]
        }
      }
    }
    setLocalSelectedSkills(dedupeSkills(updatedSkills))
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpenSkillDialog(false)}
      maxWidth="md"
      sx={{
        '.MuiDialog-paper': {
          width: '430px',
          p: 2
        }
      }}>
      <DialogTitle>{t('positions.skillsSection.selectSkills')}</DialogTitle>
      <DialogContent>
        <CustomMultipleSelect
          options={availableSkills}
          value={localSelectedSkills}
          onChange={handleChange}
          onInputChange={onSkillSearch}
          multiple
          label={t('positions.table.skills')}
          fullWidth
          sx={{ mt: 2 }}
        />
        {localSelectedSkills.length > 10 && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1 }}>
            {t('positions.skillsSection.maxSkills')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenSkillDialog(false)}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={localSelectedSkills.length > 10}>
          {t('positions.skillsSection.add')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SkillsSection
